// Tenant overview: the first page of a tenant. Admins and owners see the summary tiles (counts, storage,
// downloads). Readers and publishers see the page without them, and the console does not call /summary for them
// (the route gives 403).
import type { TenantDetail } from "../api/types";
import { useServices } from "../components/context";
import { SummaryTiles } from "../components/SummaryTiles";
import { ErrorBanner, Section } from "../components/ui";
import { useLoad } from "../components/useLoad";
import { formatBytes, formatTime } from "../lib/format";
import { pendingItems } from "../lib/grace";
import { atLeast } from "../lib/roles";
import { isTenantLimits, limitText, planName } from "../lib/plans";
import { routeHash } from "../lib/router";
import { summaryStorage, summaryTiles } from "../lib/summary";

export const SUMMARY_HIDDEN_TEXT = "Only admins and owners see the tenant counts.";

/** Storage by package type (api.md 4.1). Go comes from registry 1.1.0; an older registry has no `go`. */
export function storageByTypeText(b: { cargo: number; oci: number; go?: number }): string {
  const parts = [`crates ${formatBytes(b.cargo)}`, `containers ${formatBytes(b.oci)}`];
  if (typeof b.go === "number") parts.push(`Go modules ${formatBytes(b.go)}`);
  return `Storage by type: ${parts.join(", ")}. The plan quota counts ${parts.length === 2 ? "both" : "all"}.`;
}

export function Overview({ tenant }: { tenant: TenantDetail }) {
  const { api } = useServices();
  const allowed = atLeast(tenant.role, "admin");
  const summary = useLoad(
    () => (allowed ? api.getTenantSummary(tenant.slug) : Promise.resolve(null)),
    [tenant.slug, allowed],
  );
  // Read the tenant detail again, so the pending deletion counts are current after a delete on another page.
  const detail = useLoad(() => api.getTenant(tenant.slug), [tenant.slug]);
  const pending = pendingItems((detail.data ?? tenant).pending_deletions);
  // Plan limits from tenant detail (ADR-0030 section 8). Another shape, or an older registry: no limits rows.
  const rawLimits = (detail.data ?? tenant).limits;
  const limits = isTenantLimits(rawLimits) ? rawLimits : null;
  // api.md 4.1 (registry 0.18.0): bytes per package type. The quota stays one number.
  const usage = (detail.data ?? tenant).usage;
  const byType = usage?.storage_by_type;
  // ADR-0038 section 10: Go requests outside the tenant's module paths. Shown only when the registry sends it.
  const goMisses = usage?.go_miss_ops;
  const limitRows = limits
    ? (
        [
          ["Members", limits.members],
          ["Principals", limits.principals],
          ["Namespaces", limits.namespaces],
        ] as const
      ).filter(([, u]) => u.max !== null)
    : [];

  return (
    <Section title="Overview">
      <p className="muted">
        You are {tenant.role === "admin" || tenant.role === "owner" ? "an" : "a"} {tenant.role}. Plan{" "}
        {planName(tenant.plan)}
        {tenant.plan === "trial" && tenant.trial_ends_at
          ? `, until ${formatTime(tenant.trial_ends_at).slice(0, 10)}`
          : ""}
        .
      </p>
      {allowed ? (
        <>
          <ErrorBanner error={summary.error} />
          {summary.loading && !summary.data && <p className="muted">Loading...</p>}
          {summary.data && (
            <SummaryTiles
              tiles={summaryTiles(summary.data)}
              storage={
                limits
                  ? { usedBytes: limits.storage_bytes.used, quotaBytes: limits.storage_bytes.max }
                  : summaryStorage(summary.data)
              }
            />
          )}
        </>
      ) : (
        <p className="muted">{SUMMARY_HIDDEN_TEXT}</p>
      )}
      {byType && (
        <p className="muted small" data-testid="storage-by-type">
          {storageByTypeText(byType)}
        </p>
      )}
      {typeof goMisses === "number" && (
        <p className="muted small" data-testid="go-misses">
          Go misses this month: {goMisses.toLocaleString("en-US")}. These are requests for paths outside the
          Go module paths of this tenant. They do not count against the read quota.
        </p>
      )}
      {limitRows.length > 0 && (
        <section className="plan-use" aria-label="Plan limits">
          <h3>Limits of the {planName(limits!.plan)} plan</h3>
          <ul>
            {limitRows.map(([label, u]) => (
              <li key={label} data-testid={`limit-${label.toLowerCase()}`}>
                <span>{label}</span> <span>{limitText(u)}</span>
              </li>
            ))}
          </ul>
        </section>
      )}
      {pending.length > 0 && (
        <section className="pending-summary" aria-label="Pending deletion">
          <h3>Pending deletion</h3>
          <p className="muted small">
            These objects stopped working. You can restore them until the purge time on their page.
          </p>
          <ul>
            {pending.map((p) => (
              <li key={p.text}>
                {p.page ? (
                  <a href={routeHash({ name: "tenant", tenant: tenant.slug, page: p.page, type: p.type })}>
                    {p.text}
                  </a>
                ) : (
                  p.text
                )}
              </li>
            ))}
          </ul>
        </section>
      )}
    </Section>
  );
}
